import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Eye, EyeOff, Smartphone } from 'lucide-react';

export default function LoginPage() {
  const [form, setForm] = useState({ email: '', password: '', tenant_slug: '' });
  const [twoFAStep, setTwoFAStep] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [twoFAMethod, setTwoFAMethod] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState({ login_logo: null, app_name: 'Your Hero' });
  const { login } = useAuth();
  const navigate = useNavigate();

  // Lade globales Branding (Login-Logo)
  useEffect(() => {
    api.get('/public/branding')
      .then(res => setBranding(res.data))
      .catch(() => {}); // Ignoriere Fehler, verwende Standard
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.tenant_slug) {
      toast.error('Alle Felder ausfüllen');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/login', form);
      if (res.data.requires_2fa) {
        setTempToken(res.data.temp_token);
        setTwoFAMethod(res.data.two_fa_method);
        setTwoFAStep(true);
        toast.info('2-Faktor-Authentifizierung erforderlich');
      } else {
        login(res.data.access_token, res.data.user, res.data.tenant);
        toast.success(`Willkommen, ${res.data.user.name}!`);
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    if (!twoFACode) { toast.error('Code eingeben'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/2fa/verify', { temp_token: tempToken, code: twoFACode });
      login(res.data.access_token, res.data.user, res.data.tenant);
      toast.success(`Willkommen, ${res.data.user.name}!`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ungültiger Code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex w-1/2 bg-foreground flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            {branding.login_logo ? (
              <img src={branding.login_logo} alt="Logo" className="h-12 max-w-[200px] object-contain" />
            ) : (
              <>
                <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
                  <Shield className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="font-heading font-black text-2xl text-background tracking-tight">Your Hero</span>
              </>
            )}
          </Link>
        </div>
        <div>
          <h1 className="font-heading font-black text-5xl text-background leading-tight mb-4">
            Liegenschaften<br />im Griff.
          </h1>
          <p className="text-background/60 text-lg font-sans">
            Professionelles CRM für Projektverwaltung und Facility Management.
          </p>
        </div>
        <div className="flex gap-8">
          {[['Simple', 'Phasen-Workflow'], ['100%', 'Schweizer Produkt'], ['PDF', 'Automatisch']].map(([val, label]) => (
            <div key={label}>
              <p className="font-heading font-black text-2xl text-background">{val}</p>
              <p className="text-xs text-background/50 font-sans">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              {branding.login_logo ? (
                <img src={branding.login_logo} alt="Logo" className="h-10 max-w-[160px] object-contain" />
              ) : (
                <>
                  <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center">
                    <Shield className="w-4 h-4 text-primary-foreground" />
                  </div>
                  <span className="font-heading font-black text-xl tracking-tight">Your Hero</span>
                </>
              )}
            </Link>
          </div>

          {!twoFAStep ? (
            <>
              <h2 className="font-heading font-bold text-2xl text-foreground mb-1">Anmelden</h2>
              <p className="text-sm text-muted-foreground mb-8">Melden Sie sich bei Ihrem Konto an</p>
              <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
                <div className="space-y-1.5">
                  <Label htmlFor="tenant_slug">Unternehmens-ID</Label>
                  <Input
                    id="tenant_slug"
                    data-testid="tenant-slug-input"
                    placeholder="ihr-unternehmen"
                    value={form.tenant_slug}
                    onChange={e => setForm(f => ({ ...f, tenant_slug: e.target.value }))}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="email-input"
                    placeholder="name@unternehmen.ch"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Passwort</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      data-testid="password-input"
                      placeholder="••••••••"
                      value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                      className="rounded-sm pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid="login-submit-btn"
                  className="w-full rounded-sm"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      Anmelden...
                    </div>
                  ) : 'Anmelden'}
                </Button>
              </form>
              <div className="mt-4 flex justify-between text-sm">
                <Link to="/forgot-password" className="text-muted-foreground hover:text-primary hover:underline">Passwort vergessen?</Link>
                <Link to="/register" className="text-primary hover:underline font-medium" data-testid="register-link">Zugang beantragen</Link>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-xl text-foreground">2-Faktor-Code</h2>
                  <p className="text-xs text-muted-foreground">
                    {twoFAMethod === 'totp' ? 'Authenticator App' : 'E-Mail OTP'}
                  </p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                {twoFAMethod === 'totp'
                  ? 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.'
                  : 'Geben Sie den per E-Mail gesendeten Code ein.'}
              </p>
              <form onSubmit={handle2FA} className="space-y-5" data-testid="2fa-form">
                <div className="space-y-1.5">
                  <Label htmlFor="2fa-code">Verifizierungscode</Label>
                  <Input
                    id="2fa-code"
                    data-testid="2fa-code-input"
                    placeholder="123456"
                    value={twoFACode}
                    onChange={e => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    className="rounded-sm font-mono text-center text-xl tracking-widest"
                    maxLength={8}
                  />
                </div>
                <Button type="submit" data-testid="2fa-submit-btn" className="w-full rounded-sm" disabled={loading}>
                  {loading ? 'Prüfen...' : 'Bestätigen'}
                </Button>
                <button
                  type="button"
                  onClick={() => { setTwoFAStep(false); setTwoFACode(''); }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Zurück
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
